import type { AssetRopaReviewListParams } from 'config/api/ropaReviews/ropaReviews.types';

import type {
  AssetRopaBooleanFilterValue,
  AssetRopaDpiaFilterState,
  AssetRopaFilterPaginationOptions,
  AssetRopaOwnerDpiaFilterState,
  AssetRopaReviewFilters,
  OwnerWorkspaceFilters,
} from './assetRopaFilter.types';
import type { AssetRopaOwnerFilterParams } from './assetRopaOwnerFilter.types';

export const mapBooleanFilterValue = (value: AssetRopaBooleanFilterValue): boolean | undefined => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

export const appendPaginationParams = (
  options: AssetRopaFilterPaginationOptions,
): Pick<AssetRopaOwnerFilterParams, 'page' | 'page_size'> => ({
  ...(options.page !== undefined ? { page: options.page } : {}),
  ...(options.pageSize !== undefined ? { page_size: options.pageSize } : {}),
});

export const appendSharedRegisterFilterParams = (
  filters: Pick<AssetRopaReviewFilters, 'search' | 'assetTypeId' | 'sensitivePersonalData' | 'thirdCountryProcessing'>,
): Pick<AssetRopaOwnerFilterParams, 'search' | 'asset_type' | 'sensitive_data' | 'third_country'> => {
  const sensitiveData = mapBooleanFilterValue(filters.sensitivePersonalData);
  const thirdCountry = mapBooleanFilterValue(filters.thirdCountryProcessing);

  return {
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.assetTypeId ? { asset_type: filters.assetTypeId } : {}),
    ...(sensitiveData !== undefined ? { sensitive_data: sensitiveData } : {}),
    ...(thirdCountry !== undefined ? { third_country: thirdCountry } : {}),
  };
};

/** Register `dpia_indication` accepts only indicated / none — not review-round warning states. */
export const mapRegisterDpiaIndication = (dpiaState: AssetRopaDpiaFilterState | AssetRopaOwnerDpiaFilterState): boolean | undefined => {
  if (dpiaState === 'indicated') {
    return true;
  }

  if (dpiaState === 'none') {
    return false;
  }

  return undefined;
};

/** Review-round `dpia_state` enum on `GET .../ropa-reviews/{round}/assets/`. */
export const mapReviewRoundDpiaState = (dpiaState: AssetRopaDpiaFilterState | string): string | undefined => {
  if (!dpiaState) {
    return undefined;
  }

  if (dpiaState === 'required_unapproved') {
    return 'dpia_required';
  }

  if (dpiaState === 'none') {
    return 'not_indicated';
  }

  return dpiaState;
};

export const ownerWorkspaceFiltersToQueryParams = (
  filters: OwnerWorkspaceFilters,
  options: AssetRopaFilterPaginationOptions = {},
): AssetRopaOwnerFilterParams => {
  const dpiaIndication = mapRegisterDpiaIndication(filters.dpiaState);

  return {
    ...appendPaginationParams(options),
    ...appendSharedRegisterFilterParams(filters),
    ...(filters.completeness ? { completeness: filters.completeness } : {}),
    ...(filters.gdprRelevance !== 'all' ? { gdpr_relevance: filters.gdprRelevance } : {}),
    ...(dpiaIndication !== undefined ? { dpia_indication: dpiaIndication } : {}),
  };
};

export const assetRopaReviewRegisterFiltersToQueryParams = (
  filters: AssetRopaReviewFilters,
  options: AssetRopaFilterPaginationOptions = {},
): AssetRopaOwnerFilterParams => {
  const dpiaIndication = mapRegisterDpiaIndication(filters.dpiaState);

  return {
    ...appendPaginationParams(options),
    ...appendSharedRegisterFilterParams(filters),
    ...(dpiaIndication !== undefined ? { dpia_indication: dpiaIndication } : {}),
  };
};

/** Query params for `GET .../ropa-reviews/{round}/assets/` (review-round triage filters). */
export const assetRopaReviewRoundFiltersToQueryParams = (
  filters: AssetRopaReviewFilters,
  options: AssetRopaFilterPaginationOptions = {},
): AssetRopaReviewListParams => {
  const sensitiveData = mapBooleanFilterValue(filters.sensitivePersonalData);
  const thirdCountryProcessing = mapBooleanFilterValue(filters.thirdCountryProcessing);
  const dpiaState = mapReviewRoundDpiaState(filters.dpiaState);

  return {
    ...appendPaginationParams(options),
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.reviewStatus ? { review_status: filters.reviewStatus } : {}),
    ...(filters.assetTypeId ? { asset_type: filters.assetTypeId } : {}),
    ...(filters.operationalOwnerMembershipId ? { operational_owner: filters.operationalOwnerMembershipId } : {}),
    ...(filters.warningState === 'with_warnings' ? { has_warnings: true } : {}),
    ...(filters.warningState === 'no_warnings' ? { has_warnings: false } : {}),
    ...(dpiaState ? { dpia_state: dpiaState } : {}),
    ...(sensitiveData !== undefined ? { sensitive_data: sensitiveData } : {}),
    ...(thirdCountryProcessing !== undefined ? { third_country_processing: thirdCountryProcessing } : {}),
  };
};
